<template>
    <common-page :label="event?.fullTitle" :use-custom-content="true" page-class="event-width-dialog-standard">
        <q-page-sticky position="top-right" :offset="[20, 20]" v-if="event.modified && isAuthenticated">
            <q-btn fab icon="save" color="primary" @click="saveEvent" :title="ts( 'saveToCloud' )" />
        </q-page-sticky>

        <q-tabs v-model="tab" align="justify" dense active-color="primary">
            <q-route-tab name="EventConfigurationTab" :to="{ name: 'EventConfigurationTab' }" icon="event" :label="ts( 'eventInfoShortcut' )" />
            <q-route-tab name="EventStartListTab" :to="{ name: 'EventStartListTab' }" icon="list" :label="ts( 'crewListShortcut' )" />
            <q-route-tab name="EventNoticeboardTab" :to="{ name: 'EventNoticeboardTab' }" icon="info" :label="ts( 'noticeboard' )" />
            <q-route-tab
                v-for="race in event?.races" :key="race.id"
                :name="'EventRaceTab-' + race.race" :to="{ name: 'EventRaceTab', params: { raceId: race.race } }" icon="flag"
                :label="ts( 'raceNum', { num: race.race } )" />
            <q-route-tab name="EventRaceTab" :to="{ name: 'EventRaceTab' }" icon="add" :label="ts( event?.races.length > 3 ? 'newRaceShortcut' : 'newRace' )" />
            <q-route-tab name="EventResultsTab" :to="{ name: 'EventResultsTab' }" icon="check" :label="ts( 'resultsShortcut' )" />
            <q-route-tab name="EventManagementTab" :to="{ name: 'EventManagementTab' }" icon="settings" :label="ts( 'manageEventShortcut' )" />
        </q-tabs>

        <q-separator />

        <router-view
            v-slot="{ Component }"
            @log-in-and-update="emits( 'logInAndUpdate' )">
            <keep-alive>
                <div class="minh">
                    <component :is="Component" />
                </div>
            </keep-alive>
        </router-view>
    </common-page>
</template>

<script setup lang="ts">
// Purpose: solve handling one selected event (dashboard and all event navigation)

import CommonPage from "@/components/parts/commonPage.vue"
import { authenticate, checkAuth, showError } from "@/network/auth"
import { exportEvent } from "@/network/exporter"
import apiRequest from "@/network/httpClient"
import EventsRepo from "@/orm/repositories/EventsRepo"
import { ts } from "@/plugins/i18n-formatted"
import InfoBox from "@dialogs/InfoBox.vue"
import LogIn from "@dialogs/LogIn.vue"
import { Defaults, Options } from "@model/Setting"
import SettingsRepo from "@repo/SettingsRepo"
import { useRepo } from "pinia-orm"
import { Notify, QSpinnerGears, useQuasar } from "quasar"
import { computed, ref, watch, nextTick } from "vue"
import { useRoute } from "vue-router"

const route = useRoute()
const quasar = useQuasar()

const emits = defineEmits( [ 'logInAndUpdate' ] )

const isAuthenticated = computed( () => ( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) != '' ) )

const event = computed( () => ( useRepo( EventsRepo ).getEvent( String( route.params.eventId ), true ) ) )

useRepo( EventsRepo ).selectEvent( event.value.id )

const tab = ref( event.value.currentTab )

watch( tab, ( tab ) => {
    event.value.currentTab = tab
} )

async function logIn ( event, attempts = 3, error = 0 ) {
    quasar.dialog( {
        component: LogIn,
        componentProps: {
            errorMsg: showError( error )
        }
    } ).onOk( async ( payload ) => {
        const lastError = await authenticate( payload.username, payload.password )

        if ( !isAuthenticated.value ) {
            if ( attempts > 1 )
                logIn( event, attempts - 1, lastError )
            else
                quasar.dialog( {
                    component: InfoBox,
                    componentProps: {
                        message: ts( 'tooManyAttempts' )
                    }
                } )
        } else {
            await performSaveEvent()
        }
    } )
}

async function performSaveEvent () {
    const notif = Notify.create( {
        group: false,
        type: 'secondary',
        spinner: QSpinnerGears,
        message: ts( 'exportInProgress' ),
        timeout: 0
    } )

    const data = JSON.stringify( exportEvent( event.value ) )

    apiRequest( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) ).post( '/events/cloud', {
            "ctlId": event.value.ctlId,
            "eventTitle": event.value.title,
            "workFile": data
        } )
        .then( ( response ) => {
            if ( response.status === 202 ) {
                event.value?.setModified( false )

                notif( {
                    spinner: false,
                    icon: 'done',
                    message: ts( 'exportDone' ),
                    timeout: 2000
                } )
            } else {
                notif( {
                    spinner: false,
                    color: 'negative',
                    icon: 'error',
                    message: ts( 'exportFail' ),
                    timeout: 5000
                } )

                console.log( response )
            }
        } )
        .catch( ( error ) => {
            notif( {
                spinner: false,
                color: 'negative',
                icon: 'error',
                message: ts( 'exportFail' ),
                timeout: 5000
            } )

            console.log( error )
        } )
}

async function saveEvent ( event ) {
    if ( await checkAuth() !== 'auth' ) {
        await logIn( event )

        return
    }

    await performSaveEvent()
}
</script>

<style lang="sass" scoped>
.minh
  min-height: 300px
</style>

<style lang="sass">
.event-width-dialog-standard
  min-width: 1024px !important
</style>